import React from 'react'
import { GridProps } from '@mui/material'

interface GridContainerProps {
  gridContainer?: boolean | GridProps
  children?: React.ReactNode
}

/**
 * Higher Order Component (HOC) that adds Grid container functionality to any component.
 *
 * When the `gridContainer` prop is provided, the wrapped component will receive
 * Grid container styles directly applied to it with spacing=2 by default.
 * Additional Grid props like direction, spacing, and others can be passed
 * through the `gridContainer` prop for dynamic customization.
 *
 * @param {React.ComponentType<P>} Component - The component to be wrapped and enhanced with Grid container capabilities.
 * @returns {React.FC<Props>} - The wrapped component with Grid container capabilities.
 *
 * @template P - The type of the component's props.
 *
 * @example
 * const GridEnabledComponent = withGridContainer(MyComponent);
 *
 * // Basic usage with default spacing=2
 * <GridEnabledComponent gridContainer>
 *   <div>Content</div>
 * </GridEnabledComponent>
 *
 * // With custom spacing and direction
 * <GridEnabledComponent gridContainer={{ spacing: 3, direction: 'column' }}>
 *   <div>Content</div>
 * </GridEnabledComponent>
 *
 * // With other Grid props
 * <GridEnabledComponent gridContainer={{ spacing: 4, justifyContent: 'center' }}>
 *   <div>Content</div>
 * </GridEnabledComponent>
 *
 * // Without Grid container (normal behavior)
 * <GridEnabledComponent>
 *   <div>Content</div>
 * </GridEnabledComponent>
 */
const withGridContainer = <P extends object>(Component: React.ComponentType<P>) => {
  type Props = JSX.LibraryManagedAttributes<typeof Component, P & GridContainerProps>

  const WrappedComponent: React.FC<Props> = ({ gridContainer, children, ...props }) => {
    // If gridContainer prop is not provided, render the component normally
    if (!gridContainer) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return <Component {...(props as any)}>{children}</Component>
    }

    // Extract Grid props from gridContainer
    const gridProps = typeof gridContainer === 'boolean' ? { spacing: 2 } : { spacing: 2, ...gridContainer }

    // Apply Grid container styles directly to the Component
    const componentProps = {
      ...props,
      sx: {
        display: 'flex',
        flexDirection: gridProps.direction || 'row',
        gap: gridProps.spacing,
        flexWrap: 'wrap',
        ...(props as any).sx, // Allow overriding styles
      },
      ...gridProps, // Spread other Grid props that might be useful
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return <Component {...(componentProps as any)}>{children}</Component>
  }

  return WrappedComponent
}

export default withGridContainer
